import { useEffect, useRef, useState } from "react";
import { getAdminGiftCards, refundGiftCard, resendGiftCardEmail, getSiteSettings, setGiftTabVisible } from "../../../api/api-hook";
import { hubspotRecordUrl } from "../../../utils/hubspotIds";
import { getAdminSession } from "../../../utils/adminAuth";
import GiftCertificateTemplate from "../GiftCertificateTemplate";

type GiftCard = {
  id: string;
  contactId: string;
  orderId: string;
  firstName: string;
  lastName: string;
  email: string;
  promoCode: string;
  status: string;
  value: string;
  valueRaw: number;
  codeType: string;
  timesUsed: string;
  createDate: string;
  certificateUrl: string;
  transactionNumber: string;
  paymentMethod: string;
  checkNumber: string;
  miscNote: string;
  orderStage: string;
  canRefund: boolean;
};

type RefundState =
  | { status: "idle" }
  | { status: "confirm";    card: GiftCard }
  | { status: "processing"; card: GiftCard }
  | { status: "success";    card: GiftCard; refundId: string | null }
  | { status: "error";      card: GiftCard; message: string };

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

const statusBadge = (status: string) => {
  const s = status.toLowerCase();
  const isGreen    = s === "active";
  const isRed      = s === "inactive" || s === "expired";
  const isRedeemed = s === "redeemed";
  const style = isGreen
    ? { display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, backgroundColor: "rgba(25,135,84,0.1)", color: "#146c43", whiteSpace: "nowrap" as const }
    : isRed
    ? { display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, backgroundColor: "rgba(220,53,69,0.1)", color: "#b02a37", whiteSpace: "nowrap" as const }
    : isRedeemed
    ? { display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, backgroundColor: "rgba(13,110,253,0.1)", color: "#0a58ca", whiteSpace: "nowrap" as const }
    : { display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, backgroundColor: "rgba(108,117,125,0.1)", color: "#495057", whiteSpace: "nowrap" as const };
  return (
    <span style={style}>
      {status || "—"}
    </span>
  );
};

const STATUS_OPTIONS = ["Active", "Inactive", "Expired", "Redeemed"];

const MultiSelectDropdown = ({
  options,
  selected,
  onChange,
  placeholder = "Select…",
}: {
  options: string[];
  selected: string[];
  onChange: (val: string[]) => void;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === options.length
    ? "All statuses"
    : selected.join(", ");

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 160 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="form-control form-control-sm d-flex align-items-center justify-content-between"
        style={{ borderRadius: 8, cursor: "pointer", textAlign: "left", background: "#fff" }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.875rem" }}>{label}</span>
        <i className={`bi bi-chevron-${open ? "up" : "down"} ms-2`} style={{ fontSize: "0.7rem", flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 200,
          background: "#fff", border: "1px solid #dee2e6", borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)", minWidth: "100%", padding: "0.35rem 0",
        }}>
          {options.map((opt) => (
            <label key={opt} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.875rem", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} style={{ cursor: "pointer" }} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const AllGiftCards = () => {
  const [startDate,    setStartDate]    = useState<string>("");
  const [endDate,      setEndDate]      = useState<string>("");
  const [giftCards,    setGiftCards]    = useState<GiftCard[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>(["Active"]);
  const [selected,     setSelected]     = useState<GiftCard | null>(null);
  const [refund,       setRefund]       = useState<RefundState>({ status: "idle" });
  const [pageSize,     setPageSize]     = useState<20 | 50 | 100>(20);
  const [currentPage,  setCurrentPage]  = useState(1);

  type ResendState =
    | { status: "idle" }
    | { status: "confirm";    card: GiftCard }
    | { status: "sending";    card: GiftCard }
    | { status: "success";    card: GiftCard }
    | { status: "error";      card: GiftCard; message: string };
  const [resend, setResend] = useState<ResendState>({ status: "idle" });

  const handleResendConfirm = async () => {
    if (resend.status !== "confirm") return;
    const card = resend.card;
    setResend({ status: "sending", card });
    try {
      const result = await resendGiftCardEmail(card.orderId, card.email, card.id);
      if (!result.success) throw new Error(result.message || "Failed to resend.");
      setResend({ status: "success", card });
    } catch (err: any) {
      setResend({ status: "error", card, message: err?.response?.data?.message || err?.message || "Failed to resend email." });
    }
  };

  const fetchGiftCards = async (start: string, end: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAdminGiftCards(start, end);
      if (resp.success) {
        setGiftCards(resp.giftCards);
      } else {
        setError(resp.message || "Failed to load gift cards.");
        setGiftCards([]);
      }
    } catch {
      setError("Network error. Please try again.");
      setGiftCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGiftCards(startDate, endDate);
  }, [startDate, endDate]);

  const handleRefundConfirm = async () => {
    if (refund.status !== "confirm") return;
    const card = refund.card;
    setRefund({ status: "processing", card });
    try {
      const result = await refundGiftCard({
        giftCardId:    card.id,
        orderId:       card.orderId || undefined,
        transactionId: card.transactionNumber,
        amount:        card.valueRaw,
      });
      if (!result.success) throw new Error(result.message || "Refund failed.");
      // A refunded gift card can no longer be used (the server sets it to Inactive)
      const newStatus = "Inactive";
      setGiftCards((prev) =>
        prev.map((g) => g.id === card.id ? { ...g, status: newStatus, canRefund: false } : g)
      );
      if (selected?.id === card.id) setSelected((prev) => prev ? { ...prev, status: newStatus, canRefund: false } : prev);
      setRefund({ status: "success", card, refundId: result.refundId ?? null });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || err?.message || "Refund failed.";
      setRefund({ status: "error", card, message: msg });
    }
  };

  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, giftCards, pageSize]);

  // Whether the Gift tab shows on the front-end purchase page (null until loaded)
  const [giftTabVisible, setGiftTabVisibleState] = useState<boolean | null>(null);
  const [savingGiftTab,  setSavingGiftTab]       = useState(false);
  const [giftTabError,   setGiftTabError]        = useState<string | null>(null);

  useEffect(() => {
    getSiteSettings()
      .then((settings) => setGiftTabVisibleState(!!settings.giftTabVisible))
      .catch(() => setGiftTabError("Couldn't load the Gift tab setting."));
  }, []);

  const toggleGiftTab = async () => {
    if (giftTabVisible === null) return;
    setSavingGiftTab(true);
    setGiftTabError(null);
    try {
      const settings = await setGiftTabVisible(!giftTabVisible, getAdminSession()?.displayName || "Admin");
      setGiftTabVisibleState(!!settings.giftTabVisible);
    } catch {
      setGiftTabError("Couldn't save the Gift tab setting. Please try again.");
    } finally {
      setSavingGiftTab(false);
    }
  };

  const filtered = giftCards.filter((g) => {
    if (statusFilter.length > 0 && !statusFilter.includes(g.status)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      g.firstName.toLowerCase().includes(q) ||
      g.lastName.toLowerCase().includes(q) ||
      g.email.toLowerCase().includes(q) ||
      g.promoCode.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: "#023822", letterSpacing: "-0.3px" }}>All Gift Cards</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            <span style={{ fontWeight: 600, color: "#495057" }}>{filtered.length}</span> result{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggleGiftTab}
            disabled={giftTabVisible === null || savingGiftTab}
            title={giftTabVisible ? "The Gift tab is showing on the purchase page. Click to hide it." : "The Gift tab is hidden on the purchase page. Click to show it."}
            style={{ backgroundColor: "#fff", color: "#023822", border: "1px solid #023822", borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500, display: "inline-flex", alignItems: "center", opacity: giftTabVisible === null || savingGiftTab ? 0.6 : 1 }}
          >
            <i className={`bi ${giftTabVisible ? "bi-eye-slash" : "bi-eye"} me-1`} />
            {giftTabVisible === null ? "Gift Tab…" : savingGiftTab ? "Saving…" : giftTabVisible ? "Hide Gift Tab" : "Show Gift Tab"}
          </button>
          <a
            href="/admin/purchase-gift-card"
            style={{ backgroundColor: "#023822", color: "#fff", border: "none", borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
          >
            <i className="bi bi-gift me-1" />
            Purchase Gift Card
          </a>
        </div>
      </div>
      {giftTabVisible === false && (
        <div className="small mb-3" style={{ color: "#8a6d1d", background: "#fff8e6", border: "1px solid #f0e0b0", borderRadius: 8, padding: "0.5rem 0.85rem" }}>
          <i className="bi bi-eye-slash me-1" />
          The Gift tab is hidden on the purchase page, so golfers can't buy gift cards online. You can still buy them here with Purchase Gift Card.
        </div>
      )}
      {giftTabError && <div className="alert alert-danger py-2 small">{giftTabError}</div>}

      <GiftCertificateTemplate />

      {/* Filters */}
      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search name, email, promo code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 240, flex: "1 1 240px", maxWidth: 380, borderRadius: 8 }}
          />
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small text-muted text-nowrap">Status</label>
            <MultiSelectDropdown
              options={STATUS_OPTIONS}
              selected={statusFilter}
              onChange={setStatusFilter}
              placeholder="All statuses"
            />
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small text-muted text-nowrap">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150, borderRadius: 8 }}
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small text-muted text-nowrap">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150, borderRadius: 8 }}
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Table */}
      {!error && (
        <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <div className="table-responsive">
          <table
            className="table table-hover align-middle mb-0"
            style={{ fontSize: "0.84rem", cursor: "pointer" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>#</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Name</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Email</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Promo Code</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Status</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Value</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Certificate</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Date</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" }}>Transaction #</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa", textAlign: "center" }}>Resend Email</th>
                <th style={{ padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa", textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading gift cards…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-4 text-muted">
                    {search
                      ? "No gift cards match your search."
                      : statusFilter.length > 0
                      ? `No ${statusFilter.join(" / ").toLowerCase()} gift cards found.`
                      : "No gift cards found."}
                  </td>
                </tr>
              )}
              {!loading &&
                paginated.map((g, i) => (
                  <tr key={g.id} onClick={() => setSelected(g)}>
                    <td>
                      {g.contactId ? (
                        <a
                          href={hubspotRecordUrl("contact", g.contactId)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN, fontWeight: 600 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {(safePage - 1) * pageSize + i + 1}
                        </a>
                      ) : (safePage - 1) * pageSize + i + 1}
                    </td>
                    <td className="text-nowrap">
                      {g.firstName || g.lastName ? `${g.firstName} ${g.lastName}`.trim() : "—"}
                    </td>
                    <td>
                      {g.email ? (
                        <a
                          href={`mailto:${g.email}`}
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {g.email}
                        </a>
                      ) : "—"}
                    </td>
                    <td>
                      <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {g.promoCode || "—"}
                      </span>
                    </td>
                    <td>{statusBadge(g.status)}</td>
                    <td className="fw-semibold">{g.value || "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {g.certificateUrl ? (
                        <a
                          href={g.certificateUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-outline-secondary"
                          style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                        >
                          ↓ PDF
                        </a>
                      ) : "—"}
                    </td>
                    <td className="text-nowrap text-muted">{formatDateTime(g.createDate)}</td>
                    <td className="text-nowrap">
                      {g.transactionNumber
                        ? <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{g.transactionNumber}</span>
                        : g.checkNumber
                        ? <span className="text-muted small">Chk: {g.checkNumber}</span>
                        : g.miscNote
                        ? <span className="text-muted small" title={g.miscNote}>Misc: {g.miscNote.length > 20 ? g.miscNote.slice(0, 20) + "…" : g.miscNote}</span>
                        : g.paymentMethod && g.paymentMethod.toLowerCase() !== "credit card"
                        ? <span className="text-muted small">{g.paymentMethod}</span>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      {g.email && g.status.toLowerCase() === "active" && (
                        <button
                          title="Resend gift card email"
                          style={{ backgroundColor: "rgba(2,56,34,0.08)", border: "none", color: "#023822", fontSize: "1.1rem", padding: "0.2rem 0.5rem", borderRadius: 6, lineHeight: 1 }}
                          onClick={(e) => { e.stopPropagation(); setResend({ status: "confirm", card: g }); }}
                        >
                          <i className="bi bi-envelope-arrow-up-fill" />
                        </button>
                      )}
                    </td>
                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                      {g.canRefund && (
                        <button
                          style={{ backgroundColor: "rgba(220,53,69,0.08)", border: "none", color: "#b02a37", fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.65rem", borderRadius: 6, whiteSpace: "nowrap" }}
                          onClick={(e) => { e.stopPropagation(); setRefund({ status: "confirm", card: g }); }}
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Pagination */}
      {!error && !loading && filtered.length > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Rows per page:</span>
            {([20, 50, 100] as const).map((n) => (
              <button key={n} type="button" className="btn btn-sm"
                style={{ minWidth: 40, backgroundColor: pageSize === n ? BRAND_GREEN : "transparent", color: pageSize === n ? "#fff" : "#495057", border: `1px solid ${pageSize === n ? BRAND_GREEN : "#dee2e6"}`, fontWeight: pageSize === n ? 600 : 400 }}
                onClick={() => setPageSize(n)}>{n}</button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-1">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, i, arr) => { if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("…"); acc.push(p); return acc; }, [])
              .map((p, i) => p === "…"
                ? <span key={`e-${i}`} className="px-1 small text-muted">…</span>
                : <button key={p} type="button" className="btn btn-sm"
                    style={{ minWidth: 34, backgroundColor: p === safePage ? BRAND_GREEN : "transparent", color: p === safePage ? "#fff" : "#495057", border: `1px solid ${p === safePage ? BRAND_GREEN : "#dee2e6"}`, fontWeight: p === safePage ? 600 : 400 }}
                    onClick={() => setCurrentPage(p as number)}>{p}</button>
              )}
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>›</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={safePage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1040 }}
            onClick={() => setSelected(null)}
          />
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{ width: "100%", maxWidth: 440, zIndex: 1050, padding: "0 1rem" }}
          >
            <div className="card shadow border-0">
              {/* Header */}
              <div
                className="px-4 py-3 d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: BRAND_GREEN,
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                  Gift Card Details
                </span>
                <button
                  type="button"
                  className="btn-close btn-close-white btn-sm"
                  onClick={() => setSelected(null)}
                />
              </div>

              <div className="card-body p-4">
                {/* Recipient */}
                <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                  <div className="small fw-semibold mb-1" style={{ color: BRAND_GREEN }}>
                    {selected.firstName} {selected.lastName}
                  </div>
                  {selected.email && (
                    <div className="small text-muted">{selected.email}</div>
                  )}
                </div>

                {/* Gift card fields */}
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Promo Code</span>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: BRAND_GREEN,
                        letterSpacing: "0.05em",
                      }}
                    >
                      {selected.promoCode || "—"}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Status</span>
                    {statusBadge(selected.status)}
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Value</span>
                    <span className="fw-semibold" style={{ color: BRAND_GREEN }}>
                      {selected.value || "—"}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Type</span>
                    <span className="small">{selected.codeType || "—"}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Times Used</span>
                    <span className="small">{selected.timesUsed}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Payment</span>
                    <span className="small">{selected.paymentMethod || "—"}</span>
                  </div>

                  {(selected.transactionNumber || selected.checkNumber || selected.miscNote) && (
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <span className="small text-muted text-nowrap">
                        {selected.transactionNumber ? "Transaction #" : selected.checkNumber ? "Check #" : "Misc Note"}
                      </span>
                      <span style={{ fontFamily: selected.transactionNumber ? "monospace" : "inherit", fontSize: "0.82rem", textAlign: "right", wordBreak: "break-all" }}>
                        {selected.transactionNumber || selected.checkNumber || selected.miscNote}
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Created</span>
                    <span className="small text-muted">{formatDateTime(selected.createDate)}</span>
                  </div>

                  {selected.certificateUrl && (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small text-muted">Certificate</span>
                      <a
                        href={selected.certificateUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: "0.75rem" }}
                      >
                        ↓ Download PDF
                      </a>
                    </div>
                  )}
                </div>

                <div className="mt-4 text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelected(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Resend email modal */}
      {resend.status !== "idle" && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1060 }}
            onClick={() => resend.status !== "sending" && setResend({ status: "idle" })}
          />
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{ width: "100%", maxWidth: 400, zIndex: 1070, padding: "0 1rem" }}
          >
            <div className="card shadow border-0">
              <div
                className="px-4 py-3 d-flex justify-content-between align-items-center"
                style={{ backgroundColor: BRAND_GREEN, borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                  <i className="bi bi-envelope-arrow-up-fill me-2" />
                  Resend Gift Card Email
                </span>
                {resend.status !== "sending" && (
                  <button type="button" className="btn-close btn-close-white btn-sm" onClick={() => setResend({ status: "idle" })} />
                )}
              </div>

              <div className="card-body p-4">
                {resend.status === "confirm" && (
                  <>
                    <p className="small mb-3">
                      This will re-enroll <strong>{resend.card.firstName} {resend.card.lastName}</strong> in the gift card email workflow.
                    </p>
                    <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                      <div className="small text-muted">Email</div>
                      <div className="small fw-semibold">{resend.card.email}</div>
                      <div className="small text-muted mt-1">Promo Code</div>
                      <div className="small fw-semibold" style={{ fontFamily: "monospace" }}>{resend.card.promoCode}</div>
                    </div>
                    <div className="d-flex gap-2 justify-content-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setResend({ status: "idle" })}>Cancel</button>
                      <button className="btn btn-sm text-white" style={{ backgroundColor: BRAND_GREEN }} onClick={handleResendConfirm}>
                        Send Email
                      </button>
                    </div>
                  </>
                )}

                {resend.status === "sending" && (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm mb-2" style={{ color: BRAND_GREEN }} role="status" />
                    <p className="small text-muted mb-0">Triggering workflow…</p>
                  </div>
                )}

                {resend.status === "success" && (
                  <>
                    <div className="alert alert-success py-2 small mb-3">
                      Email workflow triggered successfully for <strong>{resend.card.email}</strong>.
                    </div>
                    <div className="text-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setResend({ status: "idle" })}>Close</button>
                    </div>
                  </>
                )}

                {resend.status === "error" && (
                  <>
                    <div className="alert alert-danger py-2 small mb-3">{resend.message}</div>
                    <div className="d-flex gap-2 justify-content-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setResend({ status: "idle" })}>Cancel</button>
                      <button className="btn btn-sm text-white" style={{ backgroundColor: BRAND_GREEN }} onClick={handleResendConfirm}>Retry</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Refund modal */}
      {refund.status !== "idle" && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1060 }}
            onClick={() => refund.status !== "processing" && setRefund({ status: "idle" })}
          />
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{ width: "100%", maxWidth: 420, zIndex: 1070, padding: "0 1rem" }}
          >
            <div className="card shadow border-0">
              <div
                className="px-4 py-3 d-flex justify-content-between align-items-center"
                style={{ backgroundColor: BRAND_GREEN, borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>Refund Gift Card</span>
                {refund.status !== "processing" && (
                  <button type="button" className="btn-close btn-close-white btn-sm" onClick={() => setRefund({ status: "idle" })} />
                )}
              </div>

              <div className="card-body p-4">
                {/* Confirm */}
                {refund.status === "confirm" && (
                  <>
                    <p className="mb-1 small">Are you sure you want to refund this gift card?</p>
                    <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                      <div className="small fw-semibold" style={{ color: BRAND_GREEN }}>
                        {refund.card.firstName} {refund.card.lastName}
                      </div>
                      <div className="small text-muted">{refund.card.email}</div>
                      <div className="small mt-1">
                        Code: <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{refund.card.promoCode}</span>
                      </div>
                      <div className="small">Amount: <strong>{refund.card.value}</strong></div>
                      <div className="small text-muted">Txn: {refund.card.transactionNumber}</div>
                    </div>
                    <p className="small text-danger mb-3">
                      This will process a <strong>{refund.card.value}</strong> refund via VersaPay and mark the gift card as Cancelled.
                    </p>
                    <div className="d-flex gap-2 justify-content-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setRefund({ status: "idle" })}>Cancel</button>
                      <button className="btn btn-sm btn-danger" onClick={handleRefundConfirm}>Confirm Refund</button>
                    </div>
                  </>
                )}

                {/* Processing */}
                {refund.status === "processing" && (
                  <div className="text-center py-3">
                    <div className="spinner-border text-danger mb-2" role="status" />
                    <p className="small text-muted mb-0">Processing refund…</p>
                  </div>
                )}

                {/* Success */}
                {refund.status === "success" && (
                  <>
                    <div className="alert alert-success py-2 small mb-3">
                      Refund processed successfully.
                      {refund.refundId && <div className="text-muted mt-1">Refund ID: <code>{refund.refundId}</code></div>}
                    </div>
                    <div className="text-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setRefund({ status: "idle" })}>Close</button>
                    </div>
                  </>
                )}

                {/* Error */}
                {refund.status === "error" && (
                  <>
                    <div className="alert alert-danger py-2 small mb-3">{refund.message}</div>
                    <div className="d-flex gap-2 justify-content-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setRefund({ status: "idle" })}>Cancel</button>
                      <button className="btn btn-sm btn-danger" onClick={handleRefundConfirm}>Retry</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllGiftCards;
